import React, { useEffect, useState } from 'react';
import type { NotificationDto, WidgetConfig } from './types';

interface WidgetToastProps {
    notification: NotificationDto | null;
    overlayPosition: WidgetConfig['overlayPosition'];
    onDismiss: () => void;
    onClick: () => void;
    autoHideDuration?: number;
}

/**
 * Toast notification component that appears when new notifications arrive
 * Auto-dismisses after a configured duration
 */
export const WidgetToast: React.FC<WidgetToastProps> = ({
    notification,
    overlayPosition,
    onDismiss,
    onClick,
    autoHideDuration = 5000,
}) => {
    const [isVisible, setIsVisible] = useState(false);
    const [isLeaving, setIsLeaving] = useState(false);

    useEffect(() => {
        if (notification) {
            setIsVisible(true);
            setIsLeaving(false);

            const timer = setTimeout(() => {
                handleDismiss();
            }, autoHideDuration);

            return () => clearTimeout(timer);
        }
    }, [notification, autoHideDuration]);

    const handleDismiss = () => {
        setIsLeaving(true);
        setTimeout(() => {
            setIsVisible(false);
            setIsLeaving(false);
            onDismiss();
        }, 300); // Match animation duration
    };

    const handleClick = () => {
        handleDismiss();
        onClick();
    };

    if (!notification || !isVisible) {
        return null;
    }

    const positionClass = getPositionClass(overlayPosition);

    return (
        <div
            className={`atlas-widget-toast ${positionClass} ${isLeaving ? 'atlas-widget-toast--leaving' : ''}`}
            onClick={handleClick}
            role="alert"
            aria-live="polite"
        >
            <div className="atlas-widget-toast__icon">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2ZM10 6C10.5523 6 11 6.44772 11 7V10C11 10.5523 10.5523 11 10 11C9.44772 11 9 10.5523 9 10V7C9 6.44772 9.44772 6 10 6ZM10 12.5C10.5523 12.5 11 12.9477 11 13.5C11 14.0523 10.5523 14.5 10 14.5C9.44772 14.5 9 14.0523 9 13.5C9 12.9477 9.44772 12.5 10 12.5Z" fill="currentColor" />
                </svg>
            </div>
            <div className="atlas-widget-toast__content">
                <div className="atlas-widget-toast__title">
                    {notification.title}
                </div>
                <div className="atlas-widget-toast__message">
                    {truncateMessage(notification.message, 80)}
                </div>
            </div>
            <button
                className="atlas-widget-toast__close"
                onClick={(e) => {
                    e.stopPropagation();
                    handleDismiss();
                }}
                aria-label="Dismiss notification"
            >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </button>
        </div>
    );
};

/**
 * Truncate message to specified length with ellipsis
 */
function truncateMessage(message: string, maxLength: number): string {
    if (message.length <= maxLength) {
        return message;
    }
    return message.substring(0, maxLength).trim() + '...';
}

/**
 * Get CSS class based on overlay position for proper placement
 * For sidebar positions, defaults to bottom-right for toast
 */
function getPositionClass(overlayPosition: WidgetConfig['overlayPosition']): string {
    switch (overlayPosition) {
        case 'bottom-right':
            return 'atlas-widget-toast--bottom-right';
        case 'bottom-left':
            return 'atlas-widget-toast--bottom-left';
        case 'top-right':
            return 'atlas-widget-toast--top-right';
        case 'top-left':
            return 'atlas-widget-toast--top-left';
        case 'left':
        case 'right':
            // Sidebar positions: toast appears at bottom-right
            return 'atlas-widget-toast--bottom-right';
        default:
            return 'atlas-widget-toast--bottom-right';
    }
}
